/**
 * Which parts of the API are served by the real backend.
 *
 * `samin-ex-core`'s `app/main.py` mounts **ten** user-facing routers — app,
 * market-data, auth, users, kyc, trading, wallet, transactions, notifications
 * and web — and between them they serve **68 endpoints** (69 with the ops
 * router's `/health`), counted from `/openapi.json` rather than from
 * `docs/backend-routes.md`.
 *
 * **Served is not the same as registered, and the gap is now the larger half.**
 * The list below is what this client actually reads live; everything else runs
 * on the MSW fixtures. A single global "use mocks" switch cannot express that:
 * one domain has to be live while its neighbour stays mocked.
 *
 * So the list is deliberately endpoint-level, not prefix-level — and the reason
 * has changed since it was written. It used to be that a prefix rule would
 * reach routes the backend did not serve; `/wallet/summary`,
 * `/wallet/assets/{symbol}`, `/portfolio/allocation`, `/trade/preview`,
 * `/trade/max`, sell-all and the trade receipt have all since been built. The
 * trap is now the other way round: most of those routes answer, but with
 * shapes no screen here has been aligned to, so a prefix rule would quietly
 * swap working fixtures for payloads that fail to parse. (`/trade/preview`
 * and `/trade/max` have since been checked field by field and are registered
 * under `trading`.) Registering one route at a time is
 * what keeps "the backend serves it" and "we read it" separate decisions.
 *
 * Enable domains with
 * `VITE_LIVE_DOMAINS=auth,users,kyc,trading,notifications,market,transactions,support,fiat`.
 * A request is answered by the backend only when its domain is enabled **and**
 * it matches one of that domain's routes here; everything else falls through to
 * the fixtures.
 */
export const API_DOMAINS = [
  'auth',
  'users',
  'kyc',
  'trading',
  'notifications',
  'market',
  'transactions',
  'support',
  'fiat',
] as const

export type ApiDomain = (typeof API_DOMAINS)[number]

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

interface LiveRoute {
  method: Method
  /** Anchored against the path *after* the `/api/v1` prefix. */
  pattern: RegExp
}

const route = (method: Method, pattern: RegExp): LiveRoute => ({ method, pattern })

/** An opaque path segment — ULIDs here, but the client never inspects them. */
const ID = '[^/]+'

/**
 * Served regardless of `VITE_LIVE_DOMAINS`.
 *
 * `GET /app/config` is the bootstrap call: `AppConfigGate` blocks the first
 * render on it, before any per-domain reasoning is meaningful, and it is public
 * so it needs no session. Making it a domain would mean the flags that decide
 * whether whole flows exist could be silently served by fixtures depending on
 * an env var — and the target `VITE_LIVE_DOMAINS` does not name it, so it would
 * be mocked by default. It is therefore always live, with the 404 fallback in
 * `config.ts` as the safety net for a backend that does not serve it.
 */
const ALWAYS_LIVE: readonly LiveRoute[] = [route('GET', /^\/app\/config$/)]

export const LIVE_ROUTES: Record<ApiDomain, readonly LiveRoute[]> = {
  auth: [
    route('GET', /^\/auth\/password-policy$/),
    route('POST', /^\/auth\/login$/),
    route('POST', /^\/auth\/login\/otp$/),
    // Step two of the entry flow. Unregistered, MSW would answer it from the
    // fixtures while `/auth/otp/verify` beside it went to the backend -- so a
    // real pending session would be promoted by a mock, and the token the app
    // stored afterwards would be one the service had never issued.
    route('POST', /^\/auth\/login\/password$/),
    route('POST', /^\/auth\/logout$/),
    route('POST', /^\/auth\/otp\/request$/),
    route('POST', /^\/auth\/otp\/verify$/),
    route('POST', /^\/auth\/register\/password$/),
    /* The signup field's check, from core's `feat/referral-programme` (core
     * PR #25): public, `204` for a code that would count and a bare `404`
     * otherwise, rate-limited per IP. `verify` beside it takes the optional
     * `referral_code`, which an older core ignores, so only this route waits
     * on core's deploy. */
    route('GET', new RegExp(`^/auth/referral-codes/${ID}$`)),
  ],
  users: [
    // `/me` exactly: `/me/notification-settings` belongs to notifications.
    route('GET', /^\/me$/),
    route('GET', /^\/bank-accounts$/),
    route('POST', /^\/bank-accounts$/),
    route('GET', /^\/bank-accounts\/detect-bank$/),
    route('DELETE', new RegExp(`^/bank-accounts/${ID}$`)),
    route('PATCH', new RegExp(`^/bank-accounts/${ID}/default$`)),
    /* «دعوت از دوستان», from core PR #25: the caller's code, link, rate and
     * totals, the code issued on the first call. An older core answers `404`,
     * but the screen is behind `features.referral`, which stays off until core
     * and this app are both deployed: core goes first. */
    route('GET', /^\/referral$/),
  ],
  kyc: [
    route('GET', /^\/kyc\/levels$/),
    route('GET', /^\/kyc\/status$/),
    route('GET', /^\/kyc\/selfie-challenge$/),
    route('POST', /^\/kyc\/identity$/),
    route('POST', /^\/kyc\/bank-card$/),
    route('POST', /^\/kyc\/documents$/),
    route('POST', new RegExp(`^/kyc/documents/${ID}/resubmit$`)),
    route('POST', /^\/kyc\/address$/),
    // `/kyc/provinces` and `/kyc/provinces/{id}/cities` are **not** served, so
    // the address form's dropdowns stay on the fixtures. The live route takes
    // province and city as free text, which is what the submit maps them to.
  ],
  market: [
    route('GET', /^\/assets$/),
    route('GET', new RegExp(`^/assets/${ID}$`)),
    route('GET', /^\/markets$/),
    route('GET', new RegExp(`^/markets/${ID}$`)),
    route('GET', new RegExp(`^/markets/${ID}/candles$`)),
    route('GET', /^\/prices$/),
    route('GET', new RegExp(`^/prices/${ID}/sparkline$`)),
  ],
  transactions: [
    route('GET', /^\/transactions$/),
    // `/export` also matches the `{id}` pattern below. Both are live and both
    // become passthroughs, so the overlap is harmless either way.
    route('GET', /^\/transactions\/export$/),
    route('GET', new RegExp(`^/transactions/${ID}$`)),
  ],
  trading: [
    route('GET', /^\/trades$/),
    route('GET', /^\/wallet\/balances$/),
    /* Read-only, and re-checked against the live service on 2026-10-05: both
     * answer every field `tradeMaxResponse` and `tradePreviewResponse` read,
     * plus `market` and `side`, which the schemas ignore. Mocked, they put a
     * fixture balance and fixture prices on the trade form beside the live
     * `/markets` catalogue. */
    route('GET', /^\/trade\/max$/),
    route('GET', /^\/trade\/preview$/),
    /* The confirm sheet, re-checked against core on 2026-10-06. Both bodies
     * carry every field `tradeQuoteResponse` and `tradeAcceptResponse` read,
     * and an expired quote answers `quote_expired`, which the sheet re-quotes
     * on. Accept now honours its `Idempotency-Key` (core 59c02de): a replay
     * answers the original body with 200, so the sheet's one key per quote
     * makes a double tap one trade, as it was written to. Cancel is served
     * too but nothing here calls it. */
    route('POST', /^\/trade\/quote$/),
    route('POST', new RegExp(`^/trade/quote/${ID}/accept$`)),

    /* Served but deliberately unregistered — re-checked against the current
     * service, and each reason still holds:
     *
     * `GET /portfolio` answers with raw positions only —
     * `{asset, total_amount, avg_cost, realized_pnl}` — with no totals, no
     * allocation and no fee tier. The portfolio screen is a donut, a profit
     * block and a fee ladder, and none of the three has a source there.
     * Weighting the donut by quantity instead of value would be a different
     * chart wearing the same label.
     *
     * `POST /wallet/transfer` *does* honour its key (it reaches
     * `wallet.transfer`), but `GET /wallet/transfer/quote` is not served, so
     * the sheet's «حداکثر» and its fee line have nothing live behind them.
     * Half of a money-moving form on fixtures is worse than all of it.
     *
     * See CHANGELOG.md.
     */
  ],
  notifications: [
    /* §17's whole surface, from core's `feat/alerts-complete` (core PR #22):
     * every alert carries every key, with explicit `null`s, and `PATCH` is
     * mounted, so the screen's switch, re-arm and edit all reach the service.
     * A core without that change answers the old `{market, target_price}` row,
     * which this build cannot parse, and mounts no `PATCH`: it deploys first. */
    route('GET', /^\/alerts$/),
    route('POST', /^\/alerts$/),
    route('PATCH', new RegExp(`^/alerts/${ID}$`)),
    route('DELETE', new RegExp(`^/alerts/${ID}$`)),
    /* §18's inbox, from core's `feat/notifications-complete` (core PR #24):
     * the `{items, next_cursor}` page with `tone` and `is_read` on every item,
     * the unread count behind the bell, one row read and all of them. A core
     * without that change answers a bare list of rows with `read` in place of
     * `is_read` and no `tone`, which this build cannot parse: it deploys
     * first. `read-all` cannot match the `{id}/read` pattern, which needs a
     * third segment. `/me/notification-settings` stays unregistered: no screen
     * reads it. */
    route('GET', /^\/notifications$/),
    route('GET', /^\/notifications\/unread-count$/),
    route('POST', new RegExp(`^/notifications/${ID}/read$`)),
    route('POST', /^\/notifications\/read-all$/),
  ],
  support: [
    /* §19's Goftino identity, from core 1A (`feat/support-chat`): the
     * caller's 64-hex id, the widget it belongs to and the profile core lets
     * leave. An older core does not mount it, but the chat is behind
     * `features.support_chat`, which that core does not send at all: the
     * config fails to parse, so core deploys first. */
    route('GET', /^\/support\/chat\/session$/),
    /* Core 1A-fix's bind model: `PUT` records the id the widget generated,
     * first write wins, and `DELETE` retires one Goftino refuses. Both are
     * `no-store`, and both answer `404` while the chat is off. */
    route('PUT', /^\/support\/chat\/identity$/),
    route('DELETE', /^\/support\/chat\/identity$/),
  ],
  fiat: [
    /* §15's Toman withdrawal, from core 1B: quote, create, the code bound to
     * the withdrawal and its resend, status and cancel. The surface is behind
     * `features.toman_withdrawal`, which a core without 1B does not send at
     * all, so the config fails to parse and core deploys first. `quote` also
     * matches the `{id}` pattern below; both are live, so the overlap is
     * harmless. */
    route('GET', /^\/fiat\/withdrawals\/quote$/),
    route('POST', /^\/fiat\/withdrawals$/),
    route('POST', new RegExp(`^/fiat/withdrawals/${ID}/otp$`)),
    route('POST', new RegExp(`^/fiat/withdrawals/${ID}/confirm$`)),
    route('GET', new RegExp(`^/fiat/withdrawals/${ID}$`)),
    route('POST', new RegExp(`^/fiat/withdrawals/${ID}/cancel$`)),
    /* §11's Toman deposit, from core 2A: the methods, the destination, the
     * list and one deposit, all reads. Behind `features.toman_deposit`, which
     * a core without 2A does not send, so it deploys first here too. The
     * gateway, identifier and verify routes are gone and nothing calls them.
     * `methods` and `destination` also match `{id}`, harmlessly. */
    route('GET', /^\/fiat\/deposits\/methods$/),
    route('GET', /^\/fiat\/deposits\/destination$/),
    route('GET', /^\/fiat\/deposits$/),
    route('GET', new RegExp(`^/fiat/deposits/${ID}$`)),
  ],
}

function parseDomains(raw: string | undefined): ReadonlySet<ApiDomain> {
  if (!raw) return new Set()
  const named = raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
  const unknown = named.filter((name) => !API_DOMAINS.includes(name as ApiDomain))
  if (unknown.length > 0) {
    // Loud rather than silent: a typo here silently mocks a domain you meant
    // to point at the backend, and the app looks like it is working.
    console.error(
      `[api] VITE_LIVE_DOMAINS names unknown domains: ${unknown.join(', ')}. ` +
        `Known domains are ${API_DOMAINS.join(', ')}.`,
    )
  }
  return new Set(named.filter((name): name is ApiDomain => API_DOMAINS.includes(name as ApiDomain)))
}

/** Read once, at module load. */
export const LIVE_DOMAINS: ReadonlySet<ApiDomain> = parseDomains(
  import.meta.env.VITE_LIVE_DOMAINS,
)

export function isDomainLive(domain: ApiDomain): boolean {
  return LIVE_DOMAINS.has(domain)
}

/** Every route the backend answers in this build, always-live routes included. */
function enabledRoutes(): LiveRoute[] {
  const out = [...ALWAYS_LIVE]
  for (const domain of LIVE_DOMAINS) out.push(...LIVE_ROUTES[domain])
  return out
}

/**
 * True when the real backend serves this exact request. `path` is the part
 * after `/api/v1`, without a query string.
 */
export function isLiveRoute(method: string, path: string): boolean {
  const verb = method.toUpperCase()
  return enabledRoutes().some((entry) => entry.method === verb && entry.pattern.test(path))
}

/** Which domain owns a path, live or not — for logging and for the mocks. */
export function domainOf(method: string, path: string): ApiDomain | null {
  const verb = method.toUpperCase()
  for (const domain of API_DOMAINS) {
    for (const entry of LIVE_ROUTES[domain]) {
      if (entry.method === verb && entry.pattern.test(path)) return domain
    }
  }
  return null
}

/**
 * The live routes as URL matchers, for MSW to pass through.
 *
 * A catch-all gate cannot do this job: returning nothing from an MSW resolver
 * makes the request *unhandled*, which `onUnhandledRequest: 'bypass'` then
 * sends to the network — so a catch-all silently forwarded every mocked
 * domain to the backend, which 404s it. Registering one passthrough per live
 * route means a mocked request never matches the gate at all.
 */
export function liveRouteMatchers(): { method: Method; matcher: RegExp }[] {
  return enabledRoutes().map((entry) => {
    const body = entry.pattern.source.replace(/^\^/, '').replace(/\$$/, '')
    return {
      method: entry.method,
      matcher: new RegExp(`/api/v1${body}(?:\\?.*)?$`),
    }
  })
}
